import React, {
  useEffect,
} from 'react';
import {
  Row,
  Table,
} from '@tanstack/react-table';
import {
  useMedia,
} from 'core/hooks';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import Name from 'pages/Dashboard/components/Name';
import {
  ChargesRowData,
} from 'pages/Dashboard/pages/Billing/types';
import {
  ChargesPanelInfo,
  ChargesPanelTab,
  toChargesPanelInfo,
} from 'pages/Dashboard/pages/Billing/Charges/chargesPanelInfo';
import RowActions from 'pages/Dashboard/pages/Billing/Charges/columns/RowActions';
import TotalCell from 'pages/Dashboard/pages/Billing/Charges/columns/TotalCell';
import isNil from 'lodash/isNil';
import NotesIcon from 'core/icons/notes';
import noop from 'lodash/noop';
import Details from 'pages/Dashboard/pages/Billing/Charges/columns/Details';
import InsuranceBalanceExplanation from 'pages/Dashboard/pages/Ledger/InsuranceBalanceExplanation';
import InsuranceBalanceColumnTitle from 'pages/Dashboard/pages/Ledger/InsuranceBalanceExplanation/InsuranceBalanceColumnTitle';
import {
  defaultNegativeClass,
  defaultPositiveClass,
} from 'pages/Dashboard/pages/Billing/AmountTag';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  useChargeDetails,
} from 'pages/Dashboard/pages/Billing/Charges/RowDetailsProvider';
import {
  ExpandAllButton,
  ExpandRowButton,
  useExpandAllDetails,
} from 'pages/Dashboard/pages/Billing/Charges/columns/ExpandToggle';

export type OpenChargesPanel = (row: ChargesRowData, tab: ChargesPanelTab) => void;

export function getChargesPanelInfo(row: ChargesRowData, tab: ChargesPanelTab): ChargesPanelInfo {
  return toChargesPanelInfo({
    encounterId: row.encounterId,
    patientId: row.patientId,
    visitDate: row.visitDateTime,
    claimId: row.masterClaimId,
    tab,
  });
}

type ToggleRowCellProps = {
  row: Row<ChargesRowData>;
  table: Table<ChargesRowData>;
};

function ToggleRowCell({ row, table }: ToggleRowCellProps) {
  const { requestDetails, detailsById } = useChargeDetails();
  const encounterId = row.original.encounterId ?? 0;
  const expandable = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const isAllRowsExpanded = table.getIsAllRowsExpanded();
  const { isChild } = row.original;

  useEffect(() => {
    if (
      !isAllRowsExpanded
      && isExpanded
      && expandable
      && encounterId > 0
      && isNil(detailsById[encounterId])
    ) {
      requestDetails([encounterId]);
    }
  }, [
    isAllRowsExpanded,
    isExpanded,
    expandable,
    encounterId,
    detailsById,
    requestDetails,
  ]);

  return !isChild && (
    <ExpandRowButton
      expandable={expandable}
      isExpanded={isExpanded}
      onToggle={(event) => {
        event.stopPropagation();
        row.toggleExpanded();
      }}
    />
  );
}

type PatientCellProps = {
  row: Row<ChargesRowData>;
};

function PatientCell({ row }: PatientCellProps) {
  const { getMediaPathById } = useMedia();
  const src = getMediaPathById(row.original.patient?.patientId ?? 0) ?? '';

  return !row.original.isChild && (
    <div className="w-full !truncate">
      <Name
        {...row.original.patient?.formattedNames}
        placeholder={DEFAULT_EMPTY_VALUE}
        image={{ src }}
        link={{ Icon: NotesIcon, onClick: noop }}
      />
    </div>
  );
}

type RowWithEncounterId = {
  encounterId?: number;
};

type ToggleAllColumnTitleProps<T> = {
  table: Table<T>;
};

function ToggleAllColumnTitle<T>({ table }: ToggleAllColumnTitleProps<T>) {
  const { requestDetails } = useChargeDetails();

  useExpandAllDetails(
    table,
    requestDetails,
    (row) => (row as RowWithEncounterId).encounterId ?? 0,
  );

  return <ExpandAllButton table={table} />;
}

export function getToggleColumnTitle<T>(table: Table<T>) {
  return <ToggleAllColumnTitle table={table} />;
}

const toggleRowColumn: ColumnDef<ChargesRowData> = {
  id: 'toggleRow',
  size: 48,
  columnTitle: getToggleColumnTitle,
  cell: ({ row, table }) => <ToggleRowCell row={row} table={table} />,
};

export const patientColumn: ColumnDef<ChargesRowData> = {
  id: 'patient',
  accessorKey: 'patient',
  minSize: 110,
  size: 220,
  className: 'mt-1',
  columnTitle: 'Patient',
  cell: ({ row }) => <PatientCell row={row} />,
};

const detailsColumn: ColumnDef<ChargesRowData> = {
  id: 'details',
  accessorKey: 'encounterId',
  className: 'mt-2',
  meta: { autoSized: true },
  columnTitle: 'Details',
  cell: ({ row }) => <Details {...row.original} isExpanded={row.getIsExpanded()} />,
};

const billedAmountColumn: ColumnDef<ChargesRowData> = {
  id: 'billedAmount',
  accessorKey: 'billedAmount',
  className: 'mt-2',
  size: 100,
  columnTitle: (
    <>
      Billed
      <br />
      Amount
    </>
  ),
  multiLineHeader: true,
  cell: ({ row }) => {
    const {
      isClaim,
      isDetailsLoading,
      billedAmount = 0,
    } = row.original;

    return isClaim || isDetailsLoading ? null : (
      <TotalCell
        {...row.original}
        total={billedAmount}
        isTag
        useColors={false}
      />
    );
  },
};

const allowedAmountColumn: ColumnDef<ChargesRowData> = {
  id: 'allowedAmount',
  size: 100,
  className: 'mt-2',
  accessorKey: 'allowedAmount',
  columnTitle: (
    <>
      Allowed
      <br />
      Amount
    </>
  ),
  multiLineHeader: true,
  cell: ({ row: { original } }) => {
    const {
      allowedAmount = 0,
      isClaim,
      isDetailsLoading,
    } = original;

    return isClaim || isDetailsLoading ? null : (
      <TotalCell
        {...original}
        total={allowedAmount}
        isTag
      />
    );
  },
};
const insurancePaymentColumn: ColumnDef<ChargesRowData> = {
  id: 'insurancePayment',
  accessorKey: 'insurancePaidAmount',
  className: 'mt-2',
  columnTitle: (
    <>
      Insurance
      <br />
      Amount
    </>
  ),
  multiLineHeader: true,
  size: 100,
  cell: ({ row: { original } }) => {
    const {
      isClaim,
      isDetailsLoading,
      visitNoteBillingCustomServiceCodeId,
      insurancePaidAmount = 0,
    } = original;

    const isCustom = !isNil(visitNoteBillingCustomServiceCodeId);

    return isClaim || isCustom || isDetailsLoading ? null : (
      <TotalCell
        {...original}
        total={insurancePaidAmount}
        isTag
      />
    );
  },
};

const insuranceBalanceColumn: ColumnDef<ChargesRowData> = {
  id: 'insuranceBalance',
  accessorKey: 'insuranceBalance',
  className: 'mt-2',
  columnTitle: (
    <InsuranceBalanceColumnTitle hintStorageKey="charges-insurance-balance-hint" />
  ),
  multiLineHeader: true,
  size: 100,
  cell: ({ row: { original } }) => (original?.isChild ? null : (
    <div className="w-full flex justify-between items-center gap-2 !text-[11px]">
      <InsuranceBalanceExplanation
        encounterId={original?.encounterId ?? 0}
        amount={original?.insuranceBalance ?? 0}
        useColors
      />
    </div>
  )),
};

const patientResponsibilityColumn: ColumnDef<ChargesRowData> = {
  id: 'patientResponsibility',
  accessorKey: 'patientResponsibility',
  className: 'mt-2',
  columnTitle: (
    <>
      Patient&rsquo;s
      <br />
      Responsibility
    </>
  ),
  multiLineHeader: true,
  size: 100,
  cell: ({ row: { original } }) => (original?.isChild ? null : (
    <TotalCell
      {...original}
      total={original?.patientResponsibility}
      isTag
      useColors={false}
    />
  )),
};

const getPatientsBalanceColumn = (onOpenPanel: OpenChargesPanel): ColumnDef<ChargesRowData> => ({
  id: 'patientsBalance',
  accessorKey: 'patientsBalance',
  className: 'mt-2',
  columnTitle: (
    <>
      Patient&rsquo;s
      <br />
      Balance
    </>
  ),
  multiLineHeader: true,
  size: 80,
  cell: ({ row: { original } }) => (original?.isChild ? null : (
    <TotalCell
      {...original}
      isTag
      onClick={(e) => {
        e.stopPropagation();
        onOpenPanel(original, 'invoice');
      }}
      total={original?.patientBalance}
      classes={{
        positive: defaultNegativeClass,
        negative: defaultPositiveClass,
        neutral: defaultPositiveClass,
      }}
    />
  )),
});

const userActionColumn: ColumnDef<ChargesRowData> = {
  id: 'userAction',
  columnTitle: '',
  size: 44,
  cell: ({ row }) => (
    <RowActions {...row.original} />
  ),
};

export function getChargesColumns(onOpenPanel: OpenChargesPanel): ColumnDef<ChargesRowData>[] {
  return [
    toggleRowColumn,
    {
      ...patientColumn,
      onClick: (e, row) => {
        e.stopPropagation();
        onOpenPanel(row.original, 'chart');
      },
    },
    detailsColumn,
    billedAmountColumn,
    allowedAmountColumn,
    insurancePaymentColumn,
    insuranceBalanceColumn,
    patientResponsibilityColumn,
    getPatientsBalanceColumn(onOpenPanel),
    userActionColumn,
  ];
}
